import { useEffect, useRef } from 'react';

/**
 * Invokes `onTrigger` when the user presses the global search shortcut:
 * Ctrl+K (Linux/Windows) or ⌘K (macOS).
 *
 * Unlike `useKeyPress`, this fires even while an input is focused, matching
 * command-palette conventions, and prevents the browser default (Ctrl+K
 * focuses the omnibox in search mode on Linux/Windows, ⌘K in Firefox on
 * macOS).
 */
export const useSearchShortcut = (onTrigger: () => void) => {
  // Hold the latest callback in a ref so the global listener is registered
  // once on mount, rather than re-binding on every render when callers pass
  // an inline callback.
  const onTriggerRef = useRef(onTrigger);
  useEffect(() => {
    onTriggerRef.current = onTrigger;
  }, [onTrigger]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (event.key.toLowerCase() !== 'k') return;
      if (!(event.metaKey || event.ctrlKey)) return;
      if (event.shiftKey || event.altKey || event.repeat) return;
      event.preventDefault();
      onTriggerRef.current();
    };
    // Capture phase: the open search combobox stops keydown propagation, so a
    // bubble-phase listener would miss Cmd/Ctrl+K while the dialog is open.
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, []);
};
